import { useEffect, useState } from "react";
import { Copy, Check, Loader2, Plus, Trash2, KeyRound, Shield, Activity, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  adminListApiKeys, adminCreateApiKey, adminRevokeApiKey, adminApiKeyUsage,
} from "@/lib/api";

/**
 * Admin section to mint / list / revoke developer API keys.
 * Pairs with the backend /api/admin/api-keys endpoints.
 *
 * Mount inside AdminDashboard as a new <TabsContent value="apikeys">.
 */
export function ApiKeysSection() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [revealedKey, setRevealedKey] = useState(null); // raw key shown ONCE
  const [copied, setCopied] = useState(false);
  const [usage, setUsage] = useState({}); // {key_id: month_to_date}
  const [form, setForm] = useState({
    name: "",
    owner_id: "",
    rate_limit_rpm: 1000,
    monthly_token_cap: 100_000_000,
    note: "",
    allowed_origins: "",
    provider: "emergent",
    nvidia_model: "meta/llama-3.3-70b-instruct",
    key_format: "sabzy",
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminListApiKeys();
      setKeys(r.data?.keys || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load API keys");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const loadUsage = async (id) => {
    try {
      const r = await adminApiKeyUsage(id);
      setUsage((u) => ({ ...u, [id]: r.data.month_to_date || {} }));
    } catch (e) {
      console.warn("loadUsage failed for key", id, e);
    }
  };

  const handleCreate = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setCreating(true);
    try {
      const r = await adminCreateApiKey({
        name: form.name.trim(),
        owner_id: form.owner_id.trim() || undefined,
        rate_limit_rpm: Number(form.rate_limit_rpm) || 1000,
        monthly_token_cap: Number(form.monthly_token_cap) || 100_000_000,
        note: form.note.trim() || undefined,
        allowed_origins: form.allowed_origins.trim() || undefined,
        provider: form.provider,
        nvidia_model: form.provider === "nvidia" ? (form.nvidia_model.trim() || undefined) : undefined,
        key_format: form.key_format,
      });
      setRevealedKey({ ...r.data, name: form.name });
      setCreateOpen(false);
      setForm({
        name: "", owner_id: "", rate_limit_rpm: 1000, monthly_token_cap: 100_000_000,
        note: "", allowed_origins: "", provider: "emergent",
        nvidia_model: "meta/llama-3.3-70b-instruct", key_format: "sabzy",
      });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create key");
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id, name) => {
    if (!window.confirm(`Revoke key "${name}"? This is permanent — the client's integration will break immediately.`)) return;
    try {
      await adminRevokeApiKey(id);
      toast.success("Key revoked");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not revoke");
    }
  };

  const handleCopyRaw = async () => {
    if (!revealedKey?.raw_key) return;
    try {
      await navigator.clipboard.writeText(revealedKey.raw_key);
      setCopied(true);
      toast.success("Key copied — paste it somewhere safe NOW");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Couldn't copy — long-press the field");
    }
  };

  const activeKeys = keys.filter((k) => !k.revoked);
  const revokedKeys = keys.filter((k) => k.revoked);

  return (
    <div className="space-y-6" data-testid="apikeys-section">
      {/* Top bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Developer API keys</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Mint a key to give a paying client programmatic access to chat, race, web-search, voice, and the Sabzy agent.
            Keys are SHA-256 hashed at rest — the raw key is shown <strong>once</strong>.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="apikeys-create-btn" className="gap-2">
          <Plus className="h-4 w-4" /> New key
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <StatCard icon={KeyRound} label="Active keys" value={activeKeys.length} />
        <StatCard icon={Shield} label="Revoked" value={revokedKeys.length} />
        <StatCard
          icon={Activity}
          label="Total calls (lifetime)"
          value={keys.reduce((s, k) => s + (k.total_calls || 0), 0).toLocaleString()}
        />
      </div>

      {/* Table */}
      <div className="rounded-xl border border-border/60 bg-card/40 backdrop-blur">
        {loading ? (
          <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading keys…
          </div>
        ) : keys.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground" data-testid="apikeys-empty">
            No keys yet. Click <strong>New key</strong> to mint your first one.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="hidden md:table-cell">Owner</TableHead>
                <TableHead className="hidden md:table-cell">Key preview</TableHead>
                <TableHead className="hidden lg:table-cell">RPM</TableHead>
                <TableHead className="hidden lg:table-cell">Monthly cap</TableHead>
                <TableHead>Usage</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {keys.map((k) => (
                <TableRow key={k.id} data-testid={`apikey-row-${k.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <div className="font-medium">{k.name}</div>
                      {k.provider === "nvidia" && (
                        <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wide text-emerald-500">
                          NVIDIA
                        </span>
                      )}
                    </div>
                    {k.note && <div className="text-[11px] text-muted-foreground">{k.note}</div>}
                    {k.allowed_origins && k.allowed_origins.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {k.allowed_origins.slice(0, 2).map((o) => (
                          <span key={o} className="rounded bg-primary/10 px-1.5 py-0.5 font-mono text-[9px] text-primary">
                            {o.replace(/^https?:\/\//, "")}
                          </span>
                        ))}
                        {k.allowed_origins.length > 2 && (
                          <span className="text-[9px] text-muted-foreground">+{k.allowed_origins.length - 2}</span>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="hidden md:table-cell font-mono text-xs text-muted-foreground">
                    {k.owner_id || "—"}
                  </TableCell>
                  <TableCell className="hidden md:table-cell font-mono text-[11px] text-muted-foreground">
                    {k.key_prefix_visible || "—"}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-xs">{k.rate_limit_rpm}</TableCell>
                  <TableCell className="hidden lg:table-cell text-xs">
                    {(k.monthly_token_cap || 0).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-xs">
                    <div>{(k.total_calls || 0).toLocaleString()} calls</div>
                    <button
                      type="button"
                      className="text-[11px] text-primary hover:underline"
                      onClick={() => loadUsage(k.id)}
                      data-testid={`apikey-usage-${k.id}`}
                    >
                      {usage[k.id]
                        ? `MTD: ${(usage[k.id].tokens || 0).toLocaleString()} tok`
                        : "load MTD"}
                    </button>
                  </TableCell>
                  <TableCell>
                    {k.revoked ? (
                      <Badge variant="destructive" className="text-[10px]">Revoked</Badge>
                    ) : (
                      <Badge className="bg-emerald-500/15 text-emerald-500 text-[10px]">Active</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {!k.revoked && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRevoke(k.id, k.name)}
                        className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                        data-testid={`apikey-revoke-${k.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md" data-testid="apikey-create-dialog">
          <DialogHeader>
            <DialogTitle>Mint a new developer API key</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="ak-name" className="text-xs">Name</Label>
              <Input
                id="ak-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. AcmeCorp production"
                className="mt-1"
                data-testid="apikey-form-name"
              />
            </div>
            <div>
              <Label htmlFor="ak-owner" className="text-xs">Owner ID (optional)</Label>
              <Input
                id="ak-owner"
                value={form.owner_id}
                onChange={(e) => setForm((f) => ({ ...f, owner_id: e.target.value }))}
                placeholder="acme-corp"
                className="mt-1 font-mono text-xs"
                data-testid="apikey-form-owner"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ak-rpm" className="text-xs">Rate limit (per min)</Label>
                <Input
                  id="ak-rpm"
                  type="number"
                  value={form.rate_limit_rpm}
                  onChange={(e) => setForm((f) => ({ ...f, rate_limit_rpm: e.target.value }))}
                  className="mt-1"
                  data-testid="apikey-form-rpm"
                />
              </div>
              <div>
                <Label htmlFor="ak-cap" className="text-xs">Monthly token cap</Label>
                <Input
                  id="ak-cap"
                  type="number"
                  value={form.monthly_token_cap}
                  onChange={(e) => setForm((f) => ({ ...f, monthly_token_cap: e.target.value }))}
                  className="mt-1"
                  data-testid="apikey-form-cap"
                />
              </div>
            </div>
            <div className="rounded-lg border border-border/60 bg-background/50 p-3 space-y-2">
              <Label className="text-xs font-semibold">LLM provider</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, provider: "emergent" }))}
                  className={`rounded-md border px-3 py-2 text-left text-xs transition ${
                    form.provider === "emergent"
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-border/60 text-muted-foreground hover:border-border"
                  }`}
                  data-testid="apikey-provider-emergent"
                >
                  <div className="font-semibold">Emergent (GPT · Claude · Gemini)</div>
                  <div className="text-[10px] text-muted-foreground">Cost: your Emergent credits</div>
                </button>
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, provider: "nvidia" }))}
                  className={`rounded-md border px-3 py-2 text-left text-xs transition ${
                    form.provider === "nvidia"
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-border/60 text-muted-foreground hover:border-border"
                  }`}
                  data-testid="apikey-provider-nvidia"
                >
                  <div className="font-semibold">NVIDIA NIM (legacy)</div>
                  <div className="text-[10px] text-muted-foreground">Cost: your NVIDIA NIM key</div>
                </button>
              </div>
              {form.provider === "nvidia" && (
                <div className="pt-1">
                  <Label htmlFor="ak-nvmodel" className="text-[11px]">NVIDIA model</Label>
                  <Input
                    id="ak-nvmodel"
                    value={form.nvidia_model}
                    onChange={(e) => setForm((f) => ({ ...f, nvidia_model: e.target.value }))}
                    placeholder="meta/llama-3.3-70b-instruct"
                    className="mt-1 font-mono text-xs"
                    data-testid="apikey-form-nvmodel"
                  />
                  <p className="mt-1 text-[10px] text-muted-foreground">
                    The client still gets a Claude-shaped response with whatever model name they sent —
                    this controls which NVIDIA-hosted model actually answers.
                  </p>
                </div>
              )}
            </div>
            <div className="rounded-lg border border-border/60 bg-background/50 p-3 space-y-2">
              <Label className="text-xs font-semibold">Visible key format</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, key_format: "sabzy" }))}
                  className={`rounded-md border px-3 py-2 text-left text-xs transition ${
                    form.key_format === "sabzy"
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-border/60 text-muted-foreground hover:border-border"
                  }`}
                  data-testid="apikey-format-sabzy"
                >
                  <div className="font-semibold font-mono">sk-sabzy_…</div>
                  <div className="text-[10px] text-muted-foreground">Sabzy-branded (default)</div>
                </button>
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, key_format: "anthropic" }))}
                  className={`rounded-md border px-3 py-2 text-left text-xs transition ${
                    form.key_format === "anthropic"
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-border/60 text-muted-foreground hover:border-border"
                  }`}
                  data-testid="apikey-format-anthropic"
                >
                  <div className="font-semibold font-mono">sk-ant-api03-…</div>
                  <div className="text-[10px] text-muted-foreground">Looks like a real Claude key</div>
                </button>
              </div>
              <p className="mt-1 text-[10px] text-muted-foreground">
                Same key, same auth — only the visible prefix changes. Hand a <code className="font-mono">sk-ant-api03-…</code> to your client and
                their frontend will look like it&apos;s calling Anthropic directly.
              </p>
            </div>
            <div>
              <Label htmlFor="ak-origins" className="text-xs">
                Allowed browser origins <span className="text-muted-foreground">(optional, comma-separated)</span>
              </Label>
              <Input
                id="ak-origins"
                value={form.allowed_origins}
                onChange={(e) => setForm((f) => ({ ...f, allowed_origins: e.target.value }))}
                placeholder="https://client.com, https://app.client.com"
                className="mt-1 font-mono text-xs"
                data-testid="apikey-form-origins"
              />
              <p className="mt-1 text-[10px] text-muted-foreground">
                Leave blank for backend-only keys. Set this if the client embeds the key in their frontend —
                requests from any other domain will be rejected with 403 even if the key leaks.
              </p>
            </div>
            <div>
              <Label htmlFor="ak-note" className="text-xs">Internal note (optional)</Label>
              <Input
                id="ak-note"
                value={form.note}
                onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                placeholder="Contract reference, billing email, etc."
                className="mt-1"
                data-testid="apikey-form-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} data-testid="apikey-form-submit">
              {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
              Mint key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reveal-once modal */}
      <Dialog open={!!revealedKey} onOpenChange={(o) => !o && setRevealedKey(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="apikey-reveal-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary" />
              Copy your API key — it won&apos;t be shown again
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-muted-foreground">
              <strong>{revealedKey?.name}</strong> · rate {revealedKey?.rate_limit_rpm}/min ·
              cap {(revealedKey?.monthly_token_cap || 0).toLocaleString()} tokens/mo.
            </p>
            <div className="flex items-stretch gap-2">
              <Input
                value={revealedKey?.raw_key || ""}
                readOnly
                onFocus={(e) => e.target.select()}
                className="font-mono text-xs"
                data-testid="apikey-reveal-input"
              />
              <Button onClick={handleCopyRaw} className="shrink-0 gap-1.5" data-testid="apikey-reveal-copy">
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? "Copied" : "Copy"}
              </Button>
            </div>
            <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-[11px] text-destructive">
              ⚠️ This key is shown <strong>once</strong>. The hash is stored, not the raw key.
              If you lose it, revoke this key and mint a new one.
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setRevealedKey(null)} data-testid="apikey-reveal-close">
              I&apos;ve saved it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border/60 bg-card/40 p-3 backdrop-blur">
      <div className="grid h-9 w-9 place-items-center rounded-lg bg-primary/10 text-primary">
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="text-base font-semibold text-foreground">{value}</div>
      </div>
    </div>
  );
}

export default ApiKeysSection;
